import verifications from '@/api/verifications'
import constants from '@/constants'
import * as types from '@/store/mutation-types'

/**
 * @fileoverview this file contains our user identity module for our Vuex store.
 * Anything that logically relates to user identity data belongs here.
 */

const state = {
  verificationData: {
    verificationId: '',
    verificationMode: 'INSESSION',
    contactInfo: ''
  },
  lookupSessionId: null,
  consumerIdentity: {
    // used for lookup and verifications call of OTP
    identityProvider: 'SRC',
    identityType: constants.IDENTITY_TYPE.EMAIL,
    identityValue: ''
  },
  verificationChannelUsed: ''
}

const getters = {}

const actions = {
  /**
   * Looks up consumer based on provided identity and sets sessionId
   *
   * @param {Vuex} store
   * @param {Object} payload
   * @param {'EMAIL'|'MOBILE_NUMBER'} payload.lookupType - type of user id
   * @param {String} payload.userId - user id, email or phone number
   */
  async lookupSessionId({ commit, dispatch }, { lookupType, userId }) {
    try {
      commit(types.SET_IDENTITY_TYPE, lookupType)
      commit(types.SET_USER_ID, userId)
      const { data } = await verifications.lookupSessionId({
        consumerIdentity: state.consumerIdentity
      })
      if (data.consumerPresent) {
        commit(types.SET_LOOKUP_SESSION_ID, data.idLookupSessionId)
      }
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * clears the lookup session id
   *
   * @param {Vuex} store
   */
  clearLookupSessionId({ commit }) {
    commit(types.SET_LOOKUP_SESSION_ID, null)
  },

  /**
   * sets the lookup session id for 401 or 409 error
   *
   * @param {Vuex} store
   */
  setLookupSessionIdFromHeader({ commit }, response = {}) {
    const wwwAuthenticate = response.headers ? response.headers['www-authenticate'] : null
    if ([401, 409].includes(response.status) && wwwAuthenticate) {
      commit(types.SET_LOOKUP_SESSION_ID, wwwAuthenticate)
    }
  },

  /**
   * Fetches the verification id to be used in `verifyCode()`.
   *
   * @param {Vuex} store
   */
  async fetchVerificationId(
    { state, commit, dispatch, rootState },
    verificationChannel = constants.VALIDATION_CHANNEL.EMAIL
  ) {
    try {
      const { data } = await verifications.getVerificationId({
        idLookupSessionId: state.lookupSessionId,
        requestedValidationChannel: {
          identityProvider: 'SRC',
          identityType: rootState.authorization
            ? constants.VALIDATION_CHANNEL.EMAIL
            : verificationChannel
        }
      })

      commit(types.SET_VERIFICATION_CHANNEL_USED, verificationChannel)
      dispatch('setVerificationData', data)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Verifies if the user entered the correct otp and stores the new authorization.
   *
   * @param {Vuex} vuex
   * @param {String} code - user-submitted OTP
   */
  async validateVerificationCode({ state, dispatch }, code) {
    try {
      const { data } = await verifications.validateCode({
        idVerificationId: state.verificationData.verificationId,
        validationData: code,
        verificationMode: state.verificationData.verificationMode
      })

      dispatch('setAuthorization', data.authorization)
    } catch (error) {
      dispatch('setServiceLayerError', error)
    }
  },

  /**
   * Sets the verification data and determines what the OTP channel was
   *
   * @param {Vuex} vuex
   * @param {Object} data - verification data object
   * @param {String} data.id - id of the verification
   * @param {String} data.maskedValidationChannel - verification maskedValidationChannel eg: u*****r@mastercard.com
   * @param {Array} data.supportedValidationChannels - additional validation channels that are supported
   *
   */
  setVerificationData({ commit }, data) {
    let { contactInfo, maskedValidationChannel, supportedValidationChannels } = data
    const supportedValidationChannel = supportedValidationChannels?.find((channel) => {
      return channel.maskedValidationChannel === maskedValidationChannel
    })
    // if there is no contactInfo, figure out where the OTP was sent to
    if (supportedValidationChannel) {
      commit(types.SET_VERIFICATION_CHANNEL_USED, supportedValidationChannel.identityType)
    }

    // Use the contactInfo if given, else use the masked data
    contactInfo = contactInfo || maskedValidationChannel

    commit(types.SET_VERIFICATION_DATA, { ...data, contactInfo })
  },

  /**
   * resets this module back to default state
   *
   * @param {Vuex} store
   */
  reset({ commit }) {
    commit(types.SET_LOOKUP_SESSION_ID, null)
    commit(types.SET_VERIFICATION_DATA, {})
    commit(types.SET_USER_ID, null)
  }
}

const mutations = {
  /**
   * Sets the user's id.
   *
   * @param {Object} state
   * @param {String} newUserId
   */
  [types.SET_USER_ID](state, newUserId) {
    state.consumerIdentity.identityValue = newUserId
  },

  /**
   * Sets type of identity lookup
   *
   * @param {Object} state
   * @param {String} identityType
   */
  [types.SET_IDENTITY_TYPE](state, identityType) {
    state.consumerIdentity.identityType = identityType
  },

  /**
   * Sets the id used for verifying the user's OTP.
   *
   * @param {Object} state
   * @param {Object} verificationData
   * @param {String} verificationData.idVerificationId
   * @param {String} verificationData.verificationMode
   * @param {Object|String} verificationData.contactInfo
   * @param {String} verificationData.contactInfo.mobileNumber
   * @param {String} verificationData.contactInfo.countryCode
   */
  [types.SET_VERIFICATION_DATA](state, data) {
    const { idVerificationId, contactInfo, verificationMode = 'INSESSION' } = data
    state.verificationData.verificationId = idVerificationId
    state.verificationData.verificationMode = verificationMode
    state.verificationData.contactInfo = contactInfo
  },

  /**
   * Sets the lookup session id to be used to retrieve the OTP verification id.
   *
   * @param {Object} state
   * @param {String} lookupSessionId
   */
  [types.SET_LOOKUP_SESSION_ID](state, lookupSessionId) {
    state.lookupSessionId = lookupSessionId
  },

  /**
   * Sets the last verification channel requested to be used
   *
   * @param {Object} state
   * @param {String} verificationChannel
   */
  [types.SET_VERIFICATION_CHANNEL_USED](state, verificationChannel) {
    state.verificationChannelUsed = verificationChannel
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
